import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MyBoothLiveVoting = () => {
    const navigate = useNavigate();
    const { appId, language, boothNo } = useAppId();
    const lang = language || 'en';

    const t = {
        en: {
            pageTitle: "My Booth",
            boothNo: "Booth No :",
            totalVoter: "Voters",
            totalVoting: "Total Voting",
            remainVoting: "Remain Voting",
            noData: "No data available.",
            loading: "Fetching Booth data...",
            searchPlaceholder: "Search by Booth No..."
        },
        hi: {
            pageTitle: "मेरा बूथ",
            boothNo: "बूथ नं :",
            totalVoter: "मतदाता",
            totalVoting: "कुल वोटिंग",
            remainVoting: "बाकी वोटिंग",
            noData: "कोई डेटा उपलब्ध नहीं है।",
            loading: "डेटा लोड हो रहा है...",
            searchPlaceholder: "बूथ नंबर से खोजें..."
        },
        gu: {
            pageTitle: "મારા બૂથ",
            boothNo: "બૂથ નં :",
            totalVoter: "મતદાર",
            totalVoting: "કુલ વોટિંગ",
            remainVoting: "બાકી વોટિંગ",
            noData: "કોઈ માહિતી ઉપલબ્ધ નથી.",
            loading: "માહિતી લોડ થઈ રહી છે...",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો..."
        }
    };

    const txt = t[lang] || t.gu;

    const [loading, setLoading] = useState(false);
    const [booths, setBooths] = useState([]);
    const [colorThresholds, setColorThresholds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const userBoothSet = React.useMemo(() => {
        const source = boothNo || localStorage.getItem('boothNo') || '';
        return new Set(
            String(source)
                .split(',')
                .map((b) => b.trim())
                .filter(Boolean)
        );
    }, [boothNo]);

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchBoothVotingData = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_voting_report`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data) {
                    const allBooths = response.data.data.result || [];
                    const filtered = userBoothSet.size > 0
                        ? allBooths.filter((booth) => userBoothSet.has(String(booth.booth_no).trim()))
                        : [];
                    setBooths(filtered);

                    const sortedColors = (response.data.data.result2 || []).sort((a, b) => a.upto_percent - b.upto_percent);
                    setColorThresholds(sortedColors);
                } else {
                    setBooths([]);
                }
            } catch (error) {
                console.error("Error fetching my booth voting report:", error);
                setBooths([]);
            } finally {
                setLoading(false);
            }
        };

        fetchBoothVotingData();
        const interval = setInterval(fetchBoothVotingData, 60000);
        return () => clearInterval(interval);
    }, [appId, navigate, userBoothSet]);

    const getHeaderColor = (percent) => {
        let selectedColor = "#d68e09";

        for (let i = 0; i < colorThresholds.length; i++) {
            if (percent <= colorThresholds[i].upto_percent) {
                selectedColor = colorThresholds[i].color_code;
                break;
            }
        }
        return selectedColor;
    };

    const filteredBooths = booths.filter(booth => {
        if (!searchTerm) return true;
        return booth.booth_no.toString().includes(searchTerm);
    });

    return (
        <div className="bw-live-voting-page">
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">

                <div className="search-wrapper mb-4 slide-up-element" style={{ maxWidth: "500px" }}>
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading && booths.length === 0 ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2 text-muted">{txt.loading}</p>
                    </div>
                ) : filteredBooths.length > 0 ? (
                    <div className="row g-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredBooths.map((booth, index) => {
                            const headerColor = getHeaderColor(booth.voting_percent);

                            return (
                                <div className="col-12 col-sm-6 col-lg-6 col-xl-4" key={index} onClick={() => navigate("/dis_booth_wise_live_voting_voter", { state: { boothNo: booth.booth_no } })}>
                                    <div className="voting-card h-100">
                                        <div className="vc-header" style={{ backgroundColor: headerColor }}>
                                            <h3 className="vc-booth-title">{txt.boothNo} {booth.booth_no}</h3>
                                            <div className="vc-percentage-pill">
                                                {booth.voting_percent}%
                                            </div>
                                        </div>

                                        <div className="vc-body">
                                            <div className="vc-stat-box bg-light-blue">
                                                <span className="vc-lbl text-blue">{txt.totalVoter}</span>
                                                <span className="vc-num text-blue">{booth.total_voter}</span>
                                            </div>
                                            <div className="vc-stat-box bg-light-green">
                                                <span className="vc-lbl text-green">{txt.totalVoting}</span>
                                                <span className="vc-num text-green">{booth.total_voting}</span>
                                            </div>
                                            <div className="vc-stat-box bg-light-red">
                                                <span className="vc-lbl text-red">{txt.remainVoting}</span>
                                                <span className="vc-num text-red">{booth.total_remain}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center text-muted p-4 bg-white rounded shadow-sm border slide-up-element">
                        {txt.noData}
                    </div>
                )}
            </div>

            <style jsx>{`
                .bw-live-voting-page { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                .search-wrapper { position: relative; width: 100%; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; }
                .search-input { width: 100%; padding: 14px 16px 14px 46px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

                .voting-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #cbd5e1;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease;
                    height: 100%;
                }
                .voting-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }

                .vc-header {
                    padding: 14px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .vc-booth-title {
                    margin: 0;
                    color: white;
                    font-size: 1.15rem;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                }
                .vc-percentage-pill {
                    background: white;
                    color: #1e293b;
                    font-weight: 800;
                    font-size: 14px;
                    padding: 4px 14px;
                    border-radius: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .vc-body {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px;
                    background: #ffffff;
                    flex: 1;
                }
                .vc-stat-box {
                    flex: 1;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 16px 8px;
                }

                .bg-light-blue { background-color: #eff6ff; }
                .text-blue { color: #1e40af; }
                .bg-light-green { background-color: #f0fdf4; }
                .text-green { color: #166534; }
                .bg-light-red { background-color: #fef2f2; }
                .text-red { color: #991b1b; }

                .vc-lbl {
                    font-size: 12px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    text-align: center;
                }
                .vc-num {
                    font-size: 24px;
                    font-weight: 800;
                    line-height: 1;
                }

                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                @media (max-width: 768px) {
                    .vc-body { gap: 8px; padding: 12px; }
                    .vc-stat-box { padding: 12px 4px; }
                    .vc-lbl { font-size: 10px; }
                    .vc-num { font-size: 20px; }
                    .vc-header { padding: 12px 16px; }
                    .vc-booth-title { font-size: 1rem; }
                    .vc-percentage-pill { font-size: 12px; padding: 4px 10px; }
                }

                @media (max-width: 480px) {
                    .vc-stat-box { padding: 10px 2px; }
                    .vc-num { font-size: 18px; }
                    .vc-lbl { font-size: 9px; }
                }
            `}</style>
        </div>
    );
};

export default MyBoothLiveVoting;
